import { useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery } from 'react-query';
import api from '../utils/api';
import { AuthContext } from './AuthContext';
import { OFFLINE_ENABLED } from '../offline/entities';
import { isEffectivelyOnline, subscribeConnectivity } from '../offline/connectivity';
import {
  ACTIVITY_WRITE_INTERVAL_MS,
  IDLE_CHECK_INTERVAL_MS,
  isScreenLocked,
  readLastActivity,
  recordActivity,
  setScreenLocked,
  shouldLock,
  subscribeScreenLock,
} from './idleLock';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

/** GET /clinics/workstation-lock, shared with the Practice settings select that writes it. */
export const WORKSTATION_LOCK_QUERY_KEY = ['clinic-workstation-lock'];

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];

/**
 * The clinic's idle-lock minutes: a number when on; null when off (the default), which is also
 * what a failed read means; undefined until the server first answers. Never from the offline
 * replica: the lock only works online. Read under DEV_BYPASS too, where only the lock itself is
 * off, so Practice settings can show and change it in local dev.
 */
export function useWorkstationLockMinutes(): number | null | undefined {
  const { user } = useContext(AuthContext);
  const { data, isError } = useQuery(
    WORKSTATION_LOCK_QUERY_KEY,
    async () => (await api.get('/clinics/workstation-lock')).data as { idleLockMinutes?: unknown },
    // Polled so a computer left open all day picks up an owner's change without a reload.
    { enabled: !!user, staleTime: 5 * 60_000, refetchInterval: 5 * 60_000 },
  );
  if (data === undefined) return isError ? null : undefined;
  const minutes = data?.idleLockMinutes;
  return typeof minutes === 'number' && minutes > 0 ? minutes : null;
}

/** Observed reachability (navigator.onLine AND no observed failure), live. */
export function useEffectivelyOnline(): boolean {
  const [online, setOnline] = useState(() => isEffectivelyOnline());
  useEffect(() => {
    const update = () => setOnline(isEffectivelyOnline());
    const unsubscribe = subscribeConnectivity(update);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      unsubscribe();
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/**
 * The idle half of the workstation lock (plan T5). Pointer, key, wheel and touch activity is
 * written to localStorage at most every 5 seconds, so every tab shares one idle clock; a
 * 15-second tick locks every tab once the clinic's time has passed. Off under DEV_BYPASS, while
 * signed out, while the clinic has it off, offline, and while the 24-hour password dialog is up.
 */
export function useIdleLock(): { locked: boolean; online: boolean } {
  const { user, loading, sessionExpiry, reauthCount } = useContext(AuthContext);
  const minutes = useWorkstationLockMinutes();
  const online = useEffectivelyOnline();
  const locked = useSyncExternalStore(subscribeScreenLock, isScreenLocked);
  const active = !DEV_BYPASS && !!user && sessionExpiry !== 'locked';
  const lastWriteRef = useRef(0);

  useEffect(() => {
    const lockIfIdle = (now: number): boolean => {
      if (!active) return false;
      const lastActivityAt = readLastActivity();
      if (lastActivityAt == null) {
        recordActivity(now);
        return false;
      }
      const lock = shouldLock({
        lastActivityAt,
        now,
        minutes: minutes ?? null,
        online,
        locked: isScreenLocked(),
      });
      if (lock) setScreenLocked(true);
      return lock;
    };
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastWriteRef.current = now;
      // A touch after the idle time has passed is not proof of who is there: it locks first.
      if (lockIfIdle(now)) return;
      // Until the setting answers, a restored session's idle time must still be judged.
      if (active && minutes === undefined) return;
      recordActivity(now);
    };
    // Background tabs throttle timers, so coming back to the tab checks at once.
    const onReturn = () => {
      if (document.visibilityState === 'visible') lockIfIdle(Date.now());
    };

    lockIfIdle(Date.now());
    const tick = window.setInterval(() => lockIfIdle(Date.now()), IDLE_CHECK_INTERVAL_MS);
    const options = { capture: true, passive: true };
    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, options));
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.clearInterval(tick);
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity, options));
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [active, minutes, online]);

  useEffect(() => {
    // Offline with offline mode on, the lock gives way to whoever is signed in (plan D5).
    if (locked && !online && OFFLINE_ENABLED) setScreenLocked(false);
  }, [locked, online]);

  useEffect(() => {
    // Nobody is signed in, so nothing is locked: a flag left behind must not greet the next sign-in.
    if (!user && !loading && locked) setScreenLocked(false);
  }, [user, loading, locked]);

  const seenReauths = useRef(reauthCount);
  // Layout effect: the lock must not repaint for a frame after the password was accepted.
  useLayoutEffect(() => {
    if (reauthCount === seenReauths.current) return;
    seenReauths.current = reauthCount;
    // A password typed into the 24-hour dialog proves who is here, so it releases the lock too.
    if (isScreenLocked()) {
      recordActivity(Date.now());
      setScreenLocked(false);
    }
  }, [reauthCount]);

  return { locked, online };
}
